import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const EASE = [0.16, 1, 0.3, 1];

export function Reveal({ children, delay = 0, y = 28, className = "", once = true }) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.15 }}
      transition={{ duration: 0.8, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function Lines({ lines, className = "", lineClass = "", delay = 0, as: Tag = "h1" }) {
  const reduce = useReducedMotion();
  return (
    <Tag className={className} aria-label={lines.join(" ")}>
      {lines.map((line, i) => (
        <span key={i} className={`block overflow-hidden ${lineClass}`}>
          <motion.span
            className="block will-change-transform"
            initial={reduce ? { opacity: 1 } : { y: "112%" }}
            animate={reduce ? { opacity: 1 } : { y: "0%" }}
            transition={{ duration: 1, delay: delay + i * 0.12, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

export function ParallaxImage({ src, alt = "", className = "", imgClassName = "", amount = 0.12 }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["-6%", "6%"]);
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        style={{ y }}
        className={`h-full w-full scale-[1.14] object-cover ${imgClassName}`}
      />
    </div>
  );
}

export function Eyebrow({ children, light = false, className = "" }) {
  return (
    <p className={`flex items-center gap-3 text-[13px] font-bold uppercase tracking-[0.2em] md:text-sm ${light ? "text-paper/70" : "text-slategray"} ${className}`}>
      <span className="inline-block h-[2px] w-8 bg-progress" aria-hidden="true" />
      {children}
    </p>
  );
}

export function CtaButton({ children, href, onClick, variant = "primary", className = "", testid }) {
  const base =
    "group inline-flex items-center gap-3 px-7 py-4 text-sm font-bold uppercase tracking-[0.14em] transition-colors duration-300";
  const styles =
    variant === "primary"
      ? "bg-progress text-white hover:bg-progress-dark"
      : variant === "dark"
      ? "bg-obsidian text-paper hover:bg-progress"
      : "border border-obsidian text-obsidian hover:border-progress hover:text-progress";
  const inner = (
    <>
      {children}
      <span className="inline-block transition-transform duration-300 group-hover:translate-x-1.5" aria-hidden="true">
        →
      </span>
    </>
  );
  if (href)
    return (
      <a data-testid={testid} href={href} onClick={onClick} className={`${base} ${styles} ${className}`}>
        {inner}
      </a>
    );
  return (
    <button data-testid={testid} type="button" onClick={onClick} className={`${base} ${styles} ${className}`}>
      {inner}
    </button>
  );
}
